import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { categories, feedPosts } from "@/mocks/guawa";
import useSeo from "@/hooks/useSeo";

type SortKey = "hot" | "new";

export default function Topics() {
  useSeo({
    title: "热议专区 - 今日曝瓜 | 分类围观瓜友讨论",
    description:
      "进入今日曝瓜热议专区，按娱乐、影视、科技、职场、体育、民生等分类浏览瓜友热议话题，支持按热度或最新排序，快速围观现场。",
  });

  const [activeCat, setActiveCat] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);

  const list = useMemo(() => {
    let items = feedPosts;
    if (activeCat !== "all") items = items.filter((p) => p.catId === activeCat);
    const sorted = [...items];
    if (sort === "hot") sorted.sort((a, b) => b.likes - a.likes);
    else sorted.sort((a, b) => (a.time > b.time ? -1 : 1));
    return sorted;
  }, [activeCat, sort]);

  const total = activeCat === "all" ? 386 : (categories.find((c) => c.id === activeCat)?.count ?? list.length);
  const totalPages = 4;

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />

      {/* 页头 */}
      <section className="border-b border-cream-200 bg-gradient-to-r from-cream-100 via-white to-cream-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
          <nav className="flex items-center gap-1.5 text-xs text-ink-400" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-medium text-ink-600 whitespace-nowrap">热议专区</span>
          </nav>
          <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                <i className="ri-chat-heart-line"></i> 瓜友讨论广场
              </span>
              <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink-900 md:text-4xl">热议专区</h1>
              <p className="mt-2 text-sm text-ink-500">按兴趣分类围观，支持按热度与最新切换排序，找到属于你的讨论现场。</p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/topic/discuss-2026"
                className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white px-4 py-2 text-sm font-semibold text-ink-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
              >
                <i className="ri-eye-line text-primary-600"></i> 本期讨论现场
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-edit-2-line"></i> 投稿参与
              </Link>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          {/* 左栏分类 */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-cream-200 bg-white p-4">
              <h2 className="px-2 text-sm font-bold text-ink-900">话题分类</h2>
              <ul className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:flex-nowrap">
                {[
                  { id: "all", name: "全部话题", icon: "ri-apps-2-line" },
                  ...categories,
                ].map((c) => {
                  const isActive = activeCat === c.id;
                  const raw = c as typeof c & { count?: number };
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveCat(c.id);
                          setPage(1);
                        }}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                          isActive
                            ? "bg-primary-500 text-white"
                            : "text-ink-600 hover:bg-primary-50 hover:text-primary-700"
                        }`}
                      >
                        <i className={`${c.icon} ${isActive ? "" : "text-primary-500"}`}></i>
                        <span className="flex-1 text-left">{c.name}</span>
                        {c.id !== "all" && (
                          <span className={`text-xs ${isActive ? "text-white/70" : "text-ink-400"}`}>
                            {(raw as { count?: number }).count ?? 0}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="mt-4 hidden rounded-2xl bg-ink-800 p-5 text-cream-100 lg:block">
              <i className="ri-megaphone-line text-2xl text-accent-400"></i>
              <p className="mt-3 text-sm font-bold text-white">理性吃瓜倡议</p>
              <p className="mt-2 text-xs leading-relaxed text-cream-200/70">
                不传谣、不造谣、不侵犯隐私，让每一场围观都建立在尊重与事实之上。
              </p>
            </div>
          </aside>

          {/* 主内容流 */}
          <div>
            {/* 排序 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 rounded-full bg-cream-100 p-1">
                {(["hot", "new"] as SortKey[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSort(k)}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                      sort === k ? "bg-white text-primary-700 shadow-soft" : "text-ink-500 hover:text-ink-700"
                    }`}
                  >
                    {k === "hot" ? "热门优先" : "最新发布"}
                  </button>
                ))}
              </div>
              <span className="text-xs text-ink-400 whitespace-nowrap">共 {total} 个话题</span>
            </div>

            {/* 列表 */}
            <div className="mt-4 flex flex-col gap-3">
              {list.map((p) => (
                <article
                  key={p.id}
                  className="group flex flex-col gap-4 rounded-2xl border border-cream-200 bg-white p-5 transition-all hover:border-primary-200 hover:shadow-soft sm:flex-row sm:items-start"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">{p.cat}</span>
                      {p.badge && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-600">
                          <i className="ri-fire-line"></i> {p.badge}
                        </span>
                      )}
                      <span className="text-xs text-ink-400">{p.time}</span>
                    </div>
                    <h3 className="mt-2 text-base font-bold leading-snug text-ink-900 md:text-lg">
                      <Link to="/topic/discuss-2026" className="transition-colors hover:text-primary-700">
                        {p.title}
                      </Link>
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{p.excerpt}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-400">
                      <span className="flex items-center gap-1"><i className="ri-eye-line text-primary-500"></i> {p.views.toLocaleString()} 围观</span>
                      <span className="flex items-center gap-1"><i className="ri-thumb-up-line text-primary-500"></i> {p.likes.toLocaleString()} 点赞</span>
                      <span className="flex items-center gap-1"><i className="ri-chat-3-line text-accent-500"></i> {p.comments.toLocaleString()} 讨论</span>
                      <Link to="/topic/discuss-2026" className="ml-auto inline-flex items-center gap-1 font-semibold text-primary-600 transition-colors hover:text-primary-700 whitespace-nowrap">
                        进入围观 <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* 分页 */}
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-cream-200 bg-white text-ink-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="上一页"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              {Array.from({ length: totalPages }).slice(0, 4).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i + 1)}
                  className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${
                    page === i + 1 ? "bg-primary-500 text-white" : "border border-cream-200 bg-white text-ink-600 hover:bg-primary-50"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-cream-200 bg-white text-ink-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="下一页"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}